import { useState } from 'react';
import { Alert, App, Button, Card, Form, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../auth/api';
import { useSession } from '../auth/session';
import { normalizeStaffPassword } from '../auth/staffPassword';
import { ApiError, NetworkError } from '../api/client';
import { tokens } from '../theme';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Khớp chính sách của backend — xem `ChangeStaffPasswordValidator`. Chỉ để báo sớm
 * cho người dùng; **server mới là nơi quyết**.
 *
 * ⚠️ Sẽ đọc từ `system_configs` khi `S2-BE-03` xong.
 */
const MINIMUM_LENGTH = 8;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const session = useSession((state) => state.session);
  const markPasswordChanged = useSession((state) => state.markPasswordChanged);
  const signOut = useSession((state) => state.signOut);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    setErrorText(null);

    try {
      await changePassword(
        values.currentPassword,
        normalizeStaffPassword(values.newPassword),
      );
      markPasswordChanged();

      // Đổi mật khẩu làm mọi refresh token cũ mất hiệu lực, kể cả của chính phiên
      // này. Bắt đăng nhập lại là cách sạch nhất thay vì để phiên nửa vời.
      // state: {} — xóa `from` cũ (có thể là /doi-mat-khau) để login xong vào trang chủ.
      message.success('Đã đổi mật khẩu. Đăng nhập lại bằng mật khẩu mới.');
      signOut();
      navigate('/login', { replace: true, state: {} });
    } catch (error) {
      setErrorText(
        error instanceof ApiError || error instanceof NetworkError
          ? error.message
          : 'Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        background: tokens.color.canvas,
        padding: tokens.space.sectionGap,
      }}
    >
      <Card style={{ width: 420, boxShadow: tokens.shadow.card }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Đổi mật khẩu</h1>

        {session?.mustChangePassword === true && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Cần đổi mật khẩu trước khi tiếp tục"
            description="Mật khẩu hiện tại do quản trị cấp và có thể đã bị người khác đọc được."
          />
        )}

        {errorText !== null && (
          <Alert style={{ marginTop: 12 }} type="error" showIcon message={errorText} />
        )}

        <Form<FormValues>
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label="Mật khẩu hiện tại"
            name="currentPassword"
            rules={[{ required: true, message: 'Chưa nhập mật khẩu hiện tại.' }]}
          >
            <Input.Password size="large" autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            // Nói rõ quy tắc TRƯỚC khi người dùng gõ, thay vì để họ bị từ chối rồi mới biết
            tooltip={`Ít nhất ${MINIMUM_LENGTH} ký tự. Một câu dễ nhớ an toàn hơn một chuỗi ngắn có ký tự đặc biệt.`}
            validateTrigger="onChange"
            rules={[
              {
                validator: async (_, value: string | undefined) => {
                  const next = normalizeStaffPassword(value ?? '');
                  if (next.length === 0) {
                    return Promise.reject(new Error('Chưa nhập mật khẩu mới.'));
                  }
                  if (next.length < MINIMUM_LENGTH) {
                    return Promise.reject(
                      new Error(`Mật khẩu mới phải có ít nhất ${MINIMUM_LENGTH} ký tự.`),
                    );
                  }
                },
              },
            ]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            label="Nhập lại mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            validateTrigger="onChange"
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_, value: string | undefined) => {
                  const confirm = normalizeStaffPassword(value ?? '');
                  const next = normalizeStaffPassword(
                    (getFieldValue('newPassword') as string | undefined) ?? '',
                  );
                  if (confirm.length === 0) {
                    return Promise.reject(new Error('Chưa nhập lại mật khẩu mới.'));
                  }
                  if (confirm.length < MINIMUM_LENGTH) {
                    return Promise.reject(
                      new Error(`Mật khẩu mới phải có ít nhất ${MINIMUM_LENGTH} ký tự.`),
                    );
                  }
                  if (confirm !== next) {
                    return Promise.reject(new Error('Hai lần nhập không khớp.'));
                  }
                },
              }),
            ]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>

          <Button type="primary" size="large" block htmlType="submit" loading={isSubmitting}>
            Đổi mật khẩu
          </Button>
        </Form>
      </Card>
    </div>
  );
}
